import { DateTime } from "luxon";
import {
	type BaseNurseShift,
	DEFAULT_DAY_SHIFT_END_HOUR,
	DEFAULT_DAY_SHIFT_START_HOUR,
	Preferences,
	type ShiftRequest,
	ShiftType,
	type SpecificPreference,
} from "./models";

const ISO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}(?:T\d{2}:\d{2}:\d{2}(?:\.\d*)?(?:Z|[-+]\d{2}:?\d{2})?)?$/;
const TIME_OFFSET_END_REGEX = /[-+]\d{2}:?\d{2}$/;

/**
 * Whether `axiosInstance` would hand this string back as a Luxon `DateTime` instead of a string (see
 * `convertIsoDates`, which every response passes through). Free text a user saves — a name, a label —
 * must not take this shape, or it comes back from the server as a date object.
 */
export const isAutoConvertedIsoDateString = (value: string): boolean => ISO_DATE_REGEX.test(value);

let defaultTimezone = "";

export function deepCloneObject<T>(obj: T): T {
	return convertIsoDates(JSON.parse(JSON.stringify(obj))) as T;
}

export const setDefaultTimezone = (tz: string) => {
	defaultTimezone = tz;
};

const luxonCreationCache = new Map<string, DateTime>();
const isAnnieFeedbackState = (data: object): boolean =>
	Object.hasOwn(data, "rating") && Object.hasOwn(data, "reason") && Object.hasOwn(data, "comment") && Object.hasOwn(data, "updated_at");

export const convertIsoDates = (data: unknown, tz?: string): unknown => {
	if (data === null || data === undefined) {
		return data;
	}

	if (typeof data === "string" && ISO_DATE_REGEX.test(data)) {
		const hasTimezone = data.includes("Z") || TIME_OFFSET_END_REGEX.test(data);
		const timezone = tz || defaultTimezone || DateTime.local().zoneName;

		const cachedDate = luxonCreationCache.get(data);
		if (cachedDate && cachedDate.zoneName === timezone) {
			return cachedDate;
		}

		const luxonDT = hasTimezone ? DateTime.fromISO(data).setZone(timezone) : DateTime.fromISO(data, { zone: timezone });
		if (luxonDT.isValid) {
			luxonCreationCache.set(data, luxonDT);
			return luxonDT;
		}

		return data;
	}

	if (Array.isArray(data)) {
		return data.map((item) => convertIsoDates(item));
	}

	if (typeof data === "object" && data !== null) {
		// Check if it's a Luxon DateTime instance
		if (DateTime.isDateTime(data)) {
			const luxonObj = data as DateTime;
			const newLuxon = luxonObj.setZone(defaultTimezone || DateTime.local().zoneName);
			return newLuxon;
		}

		const converted: { [key: string]: unknown } = {};
		const preserveFeedbackComment = isAnnieFeedbackState(data);

		for (const [key, value] of Object.entries(data)) {
			// Annie feedback comments are user-authored prose. A comment like "2026-08-19" must remain text.
			converted[key] = preserveFeedbackComment && key === "comment" && typeof value === "string" ? value : convertIsoDates(value);
		}
		return converted;
	}

	return data;
};

export const formatHoursMinutes = (decimalHours: number): string => {
	const totalMinutes = Math.round(decimalHours * 60);
	const hours = Math.floor(totalMinutes / 60);
	const minutes = totalMinutes % 60;
	return minutes === 0 ? `${hours}h` : `${hours}h ${minutes}m`;
};

/**
 * Parse "HH:mm" or "HH:mm:ss" string to { hour, minute }
 * @example parseTimeString("04:30") → { hour: 4, minute: 30 }
 *
 * For department-level times (day start, payroll start, rule times). A `shift_template` start or end is read with
 * `utils/shiftPeriod` (`shiftClockSeconds`), the grammar the backend shares.
 */
export const parseTimeString = (timeStr: string): { hour: number; minute: number } => {
	const parts = timeStr.split(":");
	return {
		hour: parseInt(parts[0], 10),
		minute: parts.length > 1 ? parseInt(parts[1], 10) : 0,
	};
};

/**
 * Check if a time falls before the logical day start (i.e. belongs to previous day).
 * @param checkTime - DateTime object to check
 * @param dayStart - Department day start time as "HH:mm" string
 * @example isBeforeDayStart(DateTime.fromObject({ hour: 4, minute: 15 }), "04:30") → true
 * @example isBeforeDayStart(DateTime.fromObject({ hour: 4, minute: 45 }), "04:30") → false
 */
const MINUTES_IN_DAY = 24 * 60;

/** True for a morning boundary (at or before noon), false for an evening one. */
const isAmTime = (dayStart: string): boolean => {
	const { hour, minute } = parseTimeString(dayStart);
	return hour * 60 + minute <= 12 * 60;
};

/**
 * The day boundary for SHIFTS: dayStart minus the department's grace.
 * "07:00" with 2h grace -> "05:00", so 06:30 counts as that morning and 03:00 still counts as the
 * night before. Wraps past midnight: "00:00" with 2h grace -> "22:00".
 *
 * Shifts only. Time off uses the raw dayStart. Mirrors `get_effective_day_start` in the backend.
 */
export const getEffectiveDayStart = (dayStart: string, graceMinutes = 0): string => {
	if (graceMinutes <= 0) return dayStart;
	const { hour, minute } = parseTimeString(dayStart);
	const shifted = (hour * 60 + minute - graceMinutes + MINUTES_IN_DAY) % MINUTES_IN_DAY;
	return `${String(Math.floor(shifted / 60)).padStart(2, "0")}:${String(shifted % 60).padStart(2, "0")}`;
};

export const isBeforeDayStart = (checkTime: DateTime, dayStart: string): boolean => {
	const { hour: dayStartHour, minute: dayStartMinute } = parseTimeString(dayStart);

	const NOON = 12 * 60;
	const checkMinutes = checkTime.hour * 60 + checkTime.minute;
	const dayStartMinutes = dayStartHour * 60 + dayStartMinute;

	// Day wraps at noon: before noon = early morning, after noon = late night
	return dayStartMinutes <= NOON ? checkMinutes < dayStartMinutes : checkMinutes >= dayStartMinutes;
};

export const isOvernightShiftStartHour = (checkTime: DateTime, dayStart: string): boolean => isBeforeDayStart(checkTime, dayStart);

/**
 * Get the logical staffing date a shift belongs to.
 * For AM day_start (e.g. 04:00): 3 AM Feb 19 → Feb 18 | 7 PM Feb 18 → Feb 18
 * For PM day_start (e.g. 20:00): 9 PM Feb 18 → Feb 19 | 3 PM Feb 19 → Feb 19
 *
 * `graceMinutes` gives shifts starting just before dayStart the day they are heading into: at 07:00
 * with 2h grace, 06:30 counts as that morning. Pass `chosenDepartment.shift_delta_minutes`. Leave it
 * out for time off, which uses the raw `dayStart`. Mirrors `get_staffing_date` in the backend.
 */
export const getStaffingDate = (dateTime: DateTime, dayStart: string, graceMinutes = 0): DateTime => {
	const boundary = getEffectiveDayStart(dayStart, graceMinutes);
	if (!isBeforeDayStart(dateTime, boundary)) return dateTime;
	return isAmTime(boundary) ? dateTime.minus({ days: 1 }) : dateTime.plus({ days: 1 });
};

export const getShiftDisplayDate = (dateTime: DateTime, dayStart: string, graceMinutes = 0): DateTime => getStaffingDate(dateTime, dayStart, graceMinutes);

export const adjustNightShiftDate = (time: DateTime, baseDate: DateTime, dayStart: string, graceMinutes = 0): DateTime => {
	// baseDate is already the staffing-day reference (the displayed date), not a wall-clock time.
	// Using getStaffingDate here would incorrectly shift midnight-valued dates (e.g. Thu 00:00 with
	// AM dayStart 04:00 maps to Wed) because midnight is "before dayStart". Use startOf("day") directly.
	const staffingDay = baseDate.startOf("day");
	const boundary = getEffectiveDayStart(dayStart, graceMinutes);

	if (isBeforeDayStart(time, boundary)) {
		const targetDay = isAmTime(boundary) ? staffingDay.plus({ days: 1 }) : staffingDay.minus({ days: 1 });
		return targetDay.set({ hour: time.hour, minute: time.minute });
	}

	return staffingDay.set({ hour: time.hour, minute: time.minute });
};

/**
 * Anchor a start/end PAIR to a staffing day, keeping the pair ordered across midnight.
 *
 * `adjustNightShiftDate` works on one instant at a time, so a shift that crosses midnight (4p→4a)
 * comes back with both ends on the same calendar day and the end BEFORE the start. Restoring the
 * day is therefore part of normalizing a range, not of normalizing a time, and every caller that
 * adjusts two related times needs it — the three that do had each written the same three lines.
 *
 * Not folded into `EditTimePopover`: that one orders the end against the user's other input rather
 * than against the normalized start, which is a different rule despite looking similar.
 */
export const adjustNightShiftRange = (
	start: DateTime,
	end: DateTime,
	baseDate: DateTime,
	dayStart: string,
	graceMinutes = 0,
): { start: DateTime; end: DateTime } => {
	const correctedStart = adjustNightShiftDate(start, baseDate, dayStart, graceMinutes);
	const correctedEnd = adjustNightShiftDate(end, baseDate, dayStart, graceMinutes);
	return { start: correctedStart, end: correctedEnd < correctedStart ? correctedEnd.plus({ days: 1 }) : correctedEnd };
};

/**
 * Keep an end's wall-clock time when a user changes a range's start, but anchor it to the new
 * start's calendar date. If that clock time is earlier than the start, it is the following day.
 */
export const reanchorEndTimeForStart = (end: DateTime, start: DateTime): DateTime => {
	const anchoredEnd = start.set({ hour: end.hour, minute: end.minute, second: end.second, millisecond: end.millisecond });
	return anchoredEnd < start ? anchoredEnd.plus({ days: 1 }) : anchoredEnd;
};

/** Resolve the end when the start changes: a selected duration wins; otherwise keep the end clock. */
export const resolveEndTimeForStartChange = (previousEnd: DateTime, newStart: DateTime, lockedHours: number | undefined): DateTime =>
	typeof lockedHours === "number" ? newStart.plus({ hours: lockedHours }) : reanchorEndTimeForStart(previousEnd, newStart);

/** Create a new DateTime reference without changing its instant or original zone. */
export const cloneDateTimePreservingZone = (value: DateTime): DateTime => DateTime.fromMillis(value.toMillis(), { zone: value.zone });

const MINUTES_PER_HOUR = 60;

/**
 * A slot given in minutes from midnight (a gap view's `startMin` / `endMin`), as times on its staffing day: each end set
 * on the day, then the pair anchored by `adjustNightShiftRange`, so a slot crossing midnight ends the next morning. The
 * times the desktop weekly gap view locks its add dialog to, and the window the mobile gaps sheets prefill with.
 */
export const slotWindowOnDay = (slot: { startMin: number; endMin: number }, date: DateTime, dayStart: string): { startTime: DateTime; endTime: DateTime } => {
	const baseDay = date.startOf("day");
	const rawStart = baseDay.set({ hour: Math.floor(slot.startMin / MINUTES_PER_HOUR), minute: slot.startMin % MINUTES_PER_HOUR });
	const rawEnd = baseDay.set({ hour: Math.floor(slot.endMin / MINUTES_PER_HOUR), minute: slot.endMin % MINUTES_PER_HOUR });
	const { start, end } = adjustNightShiftRange(rawStart, rawEnd, date, dayStart);
	return { startTime: start, endTime: end };
};

/**
 * Inverse of getStaffingDate: given a staffing date and a time-of-day,
 * return the correct calendar DateTime.
 * Times before day start belong to the overnight portion of the staffing day.
 * For AM day_start (e.g. 04:00): overnight times (before 04:00) → staffingDate + 1
 * For PM day_start (e.g. 20:00): overnight times (after 20:00) → staffingDate - 1 (actually prev calendar day)
 */
export const isAffectByDayStart = (staffingDate: DateTime, hour: number, minute: number, dayStart: string, graceMinutes = 0): DateTime => {
	const baseDay = staffingDate.startOf("day");
	const time = baseDay.set({ hour, minute, second: 0, millisecond: 0 });
	// Grace for shifts. Time off and filter matching leave it out and stay on the raw boundary.
	const boundary = getEffectiveDayStart(dayStart, graceMinutes);

	if (isBeforeDayStart(time, boundary)) {
		return isAmTime(boundary) ? time.plus({ days: 1 }) : time.minus({ days: 1 });
	}

	return time;
};

/**
 * Convert a logical staffing date to the calendar DateTime when that staffing day begins.
 * - AM dayStart (e.g. "04:00"): staffing day 3/23 starts at 3/23 04:00
 * - PM dayStart (e.g. "20:00"): staffing day 3/23 starts at 3/22 20:00 (evening before)
 *
 * @param staffingDate - A date-only DateTime representing the logical staffing day
 * @param dayStart - Department day start time as "HH:mm" string
 * @example getStaffingDayStart(DateTime.fromISO("2026-03-23"), "04:00") → 2026-03-23T04:00
 * @example getStaffingDayStart(DateTime.fromISO("2026-03-23"), "20:00") → 2026-03-22T20:00
 */
export const getStaffingDayStart = (staffingDate: DateTime, dayStart: string): DateTime => {
	const { hour, minute } = parseTimeString(dayStart);
	const isAmDayStart = hour * 60 + minute < 12 * 60;
	const calendarDay = isAmDayStart ? staffingDate : staffingDate.minus({ days: 1 });
	return calendarDay.startOf("day").set({ hour, minute });
};

/**
 * Share of a time off entry that must fall on a staffing day for that day to count as a day off.
 * Mirrors PTO_DAY_COVERAGE_RATIO in backend/tasks/calculate_pto_cost.py — keep the two in step.
 */
export const PTO_DAY_COVERAGE_RATIO = 0.5;

const HOURS_IN_DAY = 24;

/**
 * The staffing days a time off entry covers, earliest first.
 *
 * A day counts when the entry covers at least PTO_DAY_COVERAGE_RATIO of itself there. Bucketing the
 * two endpoints separately instead added a spurious day to every night absence, because the end of a
 * night is always tomorrow: 23:00 → 07:00 spills one hour past a 06:00 boundary and used to light up
 * that whole second day, so the entry rendered next to the very shift it removed as well as on it.
 *
 * The denominator is capped at one day so a multi-day entry is measured per day rather than against
 * its own total — uncapped, a three-day PTO gives each day 33% and would qualify nowhere.
 *
 * An entry too short to clear the threshold anywhere still has to land somewhere, so it falls back to
 * the day it covers most; ties go to the later day.
 * Callers must pass DateTimes already in the department timezone.
 */
export const getPtoStaffingDays = (dateFrom: DateTime, dateTo: DateTime, dayStart: string): DateTime[] => {
	const requiredHours = PTO_DAY_COVERAGE_RATIO * Math.min(dateTo.diff(dateFrom, "hours").hours, HOURS_IN_DAY);

	const days: DateTime[] = [];
	let bestDay: DateTime | undefined;
	let bestOverlap = 0;

	let staffingDay = getStaffingDate(dateFrom, dayStart).startOf("day");
	let windowStart = getStaffingDayStart(staffingDay, dayStart);

	while (windowStart < dateTo) {
		const overlapHours = DateTime.min(dateTo, windowStart.plus({ days: 1 })).diff(DateTime.max(dateFrom, windowStart), "hours").hours;

		if (overlapHours >= requiredHours) days.push(staffingDay);
		// `>=` so a tie resolves to the later day.
		if (overlapHours >= bestOverlap) {
			bestOverlap = overlapHours;
			bestDay = staffingDay;
		}

		staffingDay = staffingDay.plus({ days: 1 });
		windowStart = windowStart.plus({ days: 1 });
	}

	return days.length ? days : bestDay ? [bestDay] : [];
};

/**
 * Total hours a time off entry asks for: its staffing days × the hours-per-day stored on it.
 * Counts the same day set PTO cost bills (backend generate_date_range), so a 2-day entry at 11.5h/day
 * reads 23h rather than the per-day value.
 * Callers must pass DateTimes already in the department timezone.
 */
export const getPtoRequestedHours = (dateFrom: DateTime, dateTo: DateTime, hoursPerDay: number, dayStart: string): number =>
	getPtoStaffingDays(dateFrom, dateTo, dayStart).length * hoursPerDay;

/**
 * True when a PTO window covers whole staffing days, i.e. both boundaries sit exactly on the
 * department's day-start wall-clock. Partial (exact-times) PTOs (DAT-230) return false.
 * Callers must pass DateTimes already in the department timezone.
 */
export const isFullStaffingDayPto = (dateFrom: DateTime, dateTo: DateTime, dayStart: string): boolean => {
	const { hour, minute } = parseTimeString(dayStart);
	return dateFrom.hour === hour && dateFrom.minute === minute && dateTo.hour === hour && dateTo.minute === minute;
};

/** Format a start–end window as clock times, e.g. "10:00 AM - 2:00 PM". */
export const formatTimeRange = (from: DateTime, to: DateTime): string => `${from.toFormat("h:mm a")} - ${to.toFormat("h:mm a")}`;

/** Format a partial-day PTO window, e.g. "10:00 AM - 2:00 PM". */
export const formatPtoTimeRange = (dateFrom: DateTime, dateTo: DateTime): string => formatTimeRange(dateFrom, dateTo);

export const calculateTimeRange = (shifts: BaseNurseShift[], requests: ShiftRequest[]): { earliestTime: DateTime; latestTime: DateTime } => {
	let earliestTime: DateTime | null = null;
	let latestTime: DateTime | null = null;

	for (const shift of shifts) {
		if (!earliestTime || shift.startTime < earliestTime) earliestTime = shift.startTime;
		if (!latestTime || shift.endTime > latestTime) latestTime = shift.endTime;
	}

	for (const request of requests) {
		if (!earliestTime || request.message.shift_start < earliestTime) earliestTime = request.message.shift_start;
		if (!latestTime || request.message.shift_end > latestTime) latestTime = request.message.shift_end;
	}

	return {
		earliestTime: earliestTime || DateTime.now().startOf("day"),
		latestTime: latestTime || DateTime.now().endOf("day"),
	};
};

/**
 * Rounds a DateTime time to the nearest hour.
 * rounding logic - if minutes are less than or equal to roundingMinute, round down to the hour.
 *				  - if minutes are greater than roundingMinute, round up to the next hour.
 * @param time - The DateTime time to round.
 * @param options - Optional parameters for rounding.
 * @param options.roundingMinute - The minute threshold for rounding (default is 30).
 * @param options.includingEdgeCheck - Whether to include the edge case check (default is true).
 * @returns The rounded DateTime time.
 */
export const roundToNearestHour = (
	time: DateTime,
	options?: {
		roundingMinute?: number;
		includingEdgeCheck?: boolean;
	},
): DateTime => {
	const roundingMinute = options?.roundingMinute ?? 30;
	const includingEdgeCheck = options?.includingEdgeCheck ?? true;
	const minutes = time.minute;
	if (includingEdgeCheck ? minutes <= roundingMinute : minutes < roundingMinute) {
		return time.startOf("hour");
	}
	return time.plus({ hours: 1 }).startOf("hour");
};

export const isInPTO = (preference: SpecificPreference, shiftStart: DateTime, dayStart: string, shiftEnd?: DateTime): boolean => {
	if (preference.type !== Preferences.Avoid) return false;

	// When shift end is provided, check actual time overlap with PTO
	if (shiftEnd) {
		return shiftStart < preference.end_date && shiftEnd > preference.start_date;
	}

	// Fallback to day-level check when no shift end is available
	const day = shiftStart.startOf("day");
	return getPtoStaffingDays(preference.start_date, preference.end_date, dayStart).some((staffingDay) => staffingDay.hasSame(day, "day"));
};

export const getShiftTypeFromStartTime = (startTime: DateTime): ShiftType =>
	startTime.hour >= DEFAULT_DAY_SHIFT_START_HOUR && startTime.hour < DEFAULT_DAY_SHIFT_END_HOUR ? ShiftType.Day : ShiftType.Night;

/**
 * Most recent occurrence of `weekStartDay` (0=Sun..6=Sat) on or before *d*.
 * Use for any view-level week anchoring (schedule template grid, recurring
 * shift dialog). Backend OT bucketing uses the alias `getOtWeekStart` to make
 * intent explicit at call sites.
 */
export const getWeekStart = (d: DateTime, weekStartDay: number): DateTime => {
	const daysSince = (d.weekdayISO - weekStartDay + 7) % 7;
	return d.startOf("day").minus({ days: daysSince });
};

/**
 * OT-week start: mirrors backend `get_week_start_for_day` in backend/utils.py
 * so FE OT-week boundaries match what the BE buckets `wage_breakdown_rows` against.
 */
export const getOtWeekStart = getWeekStart;

/**
 * Days the visible week is shifted past Sunday (the BE data-week start).
 * `viewStartDay` is 0=Sun..6=Sat; result is the same value normalized to [0,6].
 * Visible position `p` sources from data-week dayIndex `p + offset` (spilling
 * into the next data-week when that exceeds 6).
 */
export const getVisibleWeekOffset = (viewStartDay: number): number => ((viewStartDay % 7) + 7) % 7;

/**
 * The VISIBLE week a calendar date falls in when weeks start `offset` days after Sunday (see
 * `getVisibleWeekOffset`). Backend weeks are Sunday-indexed; a date before the offset in its data week belongs
 * to the visible week that began in the data week before — the rule `DateSlider`'s Today reset applies. `-1`
 * when that week is not loaded: the date is outside the schedule, or it falls before the first visible day.
 */
export const visibleWeekIndexForDate = (dataWeekIndex: number, date: DateTime, offset: number): number => {
	if (dataWeekIndex < 0) return -1;
	return date.weekdayISO % 7 < offset ? dataWeekIndex - 1 : dataWeekIndex;
};

/**
 * Maps visible column `p` to its backend cell. Backend weeks are Sunday-indexed (days[0]=Sun);
 * a non-Sunday start shifts columns by `offset`, spilling past Saturday into the next week.
 * `absoluteWeekIndex` indexes the contiguous `hospitalData.weeks`, so `+1` is the real next
 * week (not a filtered bucket). `offset === 0` is the identity map → Sunday/flag-off unchanged.
 */
export const resolveDayCell = (absoluteWeekIndex: number, p: number, offset: number): { sourceWeekIndex: number; sourceDayIndex: number } => {
	// Negative index = no data-week; stay negative so the caller renders an empty cell.
	if (absoluteWeekIndex < 0) return { sourceWeekIndex: -1, sourceDayIndex: p };
	const totalIndex = p + offset;
	return totalIndex < 7
		? { sourceWeekIndex: absoluteWeekIndex, sourceDayIndex: totalIndex }
		: { sourceWeekIndex: absoluteWeekIndex + 1, sourceDayIndex: totalIndex - 7 };
};

/**
 * Index (0..masterScheduleWeeks-1) of the week containing `dateToCheck`, measured from
 * the cycle anchor `chosenDepartmentFirstSunday`. Both ends are aligned to `weekStartDay`
 * (0=Sun..6=Sat) so the diff is a whole number of weeks regardless of view start day.
 */
export const getWeekIndexOfSpecificDate = ({
	dateToCheck,
	masterScheduleWeeks,
	timezone,
	chosenDepartmentFirstSunday,
	weekStartDay = 0,
}: {
	dateToCheck: DateTime;
	masterScheduleWeeks: number;
	timezone: string;
	chosenDepartmentFirstSunday: DateTime;
	weekStartDay?: number;
}): number => {
	const firstSundayString = chosenDepartmentFirstSunday.toFormat("yyyy-MM-dd");
	const firstSundayNormalized = DateTime.fromFormat(firstSundayString, "yyyy-MM-dd").setZone(timezone);
	const ref = getWeekStart(firstSundayNormalized, weekStartDay);
	const aligned = getWeekStart(dateToCheck, weekStartDay);
	const diff = aligned.diff(ref, "days").as("days");
	return ((Math.round(diff / 7) % masterScheduleWeeks) + masterScheduleWeeks) % masterScheduleWeeks;
};

/**
 * Shift `date` forward by the cyclic distance from `fromWeekIndex` to `toWeekIndex`
 * inside a `masterScheduleWeeks`-long cycle, so the result lands in the target week-index.
 * Returns `date` unchanged when the indexes are already equal. Always non-negative weeks
 * delta (`+ masterScheduleWeeks) % masterScheduleWeeks` handles negative diffs).
 */
export const shiftDateToWeekIndex = ({
	date,
	fromWeekIndex,
	toWeekIndex,
	masterScheduleWeeks,
}: {
	date: DateTime;
	fromWeekIndex: number;
	toWeekIndex: number;
	masterScheduleWeeks: number;
}): DateTime => {
	if (fromWeekIndex === toWeekIndex) return date;
	const weeksDelta = (toWeekIndex - fromWeekIndex + masterScheduleWeeks) % masterScheduleWeeks;
	return date.plus({ weeks: weeksDelta });
};

/** Format an ISO date string as "LLL d, yyyy" (e.g. "Jun 14, 2026"). Returns "" for empty input, raw value if unparseable. */
export const formatIsoDate = (value?: string | null): string => {
	if (!value) return "";
	const dt = DateTime.fromISO(value);
	return dt.isValid ? dt.toFormat("LLL d, yyyy") : value;
};

/**
 * Format an ISO date under the ACTIVE locale — field ORDER included, not just the month name. Prefer
 * this over `formatIsoDate` for text the user reads in their own language: `toFormat("LLL d, yyyy")`
 * localizes the month word but keeps English field order ("sept 2, 2026" in Spanish), while
 * `toLocaleString` yields the locale's own arrangement ("2 sept 2026"). Raw value if unparseable, so a
 * server value that is already display-formatted passes through untouched.
 */
export const formatIsoDateLocale = (value?: string | null): string => {
	if (!value) return "";
	const dt = DateTime.fromISO(value);
	return dt.isValid ? dt.toLocaleString(DateTime.DATE_MED) : value;
};

/** Format an ISO datetime string as "LLL d, yyyy HH:mm". Returns "" for empty input, raw value if unparseable. */
export const formatIsoDateTime = (value?: string | null): string => {
	if (!value) return "";
	const dt = DateTime.fromISO(value);
	return dt.isValid ? dt.toFormat("LLL d, yyyy HH:mm") : value;
};

/**
 * Whether a shift day has passed, in the DEPARTMENT's calendar — the predicate that disables the
 * Staffing Changes panel's Post Shift tab.
 *
 * Exported because three places need the same answer and drifted when they each had their own: the tab
 * itself, and both staffing grids choosing which day carries the Annie "post an open shift" anchor. A
 * grid that picked a day this call would reject spotlighted a control the panel then refused to open.
 *
 * `dayStart` is deliberately not consulted. The tab does not consult it either, and a version that did
 * resolved to yesterday between midnight and a 06:00 `dayStart`.
 */
export const isShiftDayInPast = (date: DateTime, timezone: string): boolean => date.setZone(timezone) < DateTime.now().setZone(timezone).startOf("day");
